/**
 * Faixa de pré-venda acima do header da Loja (Plano Loja/Twotrees, Fase 8).
 *
 * Pedido do cliente: "existe uma faixa de pré-venda... quero poder dar
 * destaque em momentos do mês, também quero ter a opção de ativar ou não".
 * Duas peças: o toggle master (feature flag `presale_ribbon`, 0043) e os
 * destaques (produto + texto + janela opcional de datas + ordem, 0044) — a
 * Loja lê os dois via `storefront-marketing-runtime` e reflete sem redeploy
 * (GET /api/marketing/presale-highlights).
 *
 * Mesmo padrão de `ConfiguracoesView`/`MediaEditForm`: View pura testável +
 * página que carrega via React Query, formulário controlado sem libs extras.
 *
 * Migrado para `.tsx`/JSX real (Cluster E — Financeiro & Sistema, redesign
 * por perfil; ver `docs/architecture/ADR-0011-*.md`): troca de sintaxe
 * (`createElement` → JSX) + ícone `Flag` no título (faixa/ribbon), no mesmo
 * padrão visual de `Cupons.tsx` (ícone + texto no `h2`, nunca só o texto
 * sozinho — régua §2). Esta tela não tinha nenhum ícone antes (lacuna real).
 * Nenhum `data-*`/aria/texto/rota muda.
 */
// Import explícito de `React` — ver o mesmo comentário em `Margens.tsx`.
import React, { type FormEvent, type ReactElement } from "react";
import { Flag } from "lucide-react";
import { RecordTable } from "../components/RecordTable.js";
import { StatusPanel, errorDetail } from "../components/StatusPanel.js";
import {
  useAddPresaleHighlight,
  useCatalogDataset,
  useMarketingFeatureFlags,
  usePresaleHighlights,
  useRemovePresaleHighlight,
  useSetMarketingFeatureFlag,
  useUpdatePresaleHighlight
} from "../queries.js";
import type { MarketingFeatureFlag, PresaleHighlight } from "../api/types.js";

const CONTROL =
  "w-full rounded-control border border-border-strong bg-surface px-3 py-2 text-sm text-ink " +
  "placeholder:text-muted focus-visible:outline-none aria-[invalid=true]:border-danger";

export interface PresaleHighlightEditHandlers {
  add: (input: { productId: string; headline: string }) => void;
  toggleActive: (highlight: PresaleHighlight) => void;
  remove: (id: string) => void;
  pending: boolean;
  error: Error | null;
}

export interface MarketingFaixaPresaleViewProps {
  ribbonFlag: MarketingFeatureFlag | null;
  onToggleRibbon: (enabled: boolean) => void;
  flagPending: boolean;
  highlights: PresaleHighlight[];
  products: Array<{ productId: string; name: string }>;
  edit?: PresaleHighlightEditHandlers;
}

/** Só o que o formulário precisa do `FormData` — torna o submit testável. */
export interface PresaleHighlightFormValues {
  get(name: string): unknown;
}

/**
 * Caminho de submit REAL do formulário de adicionar destaque, isolado do
 * evento de DOM. Devolve o input enviado, ou `null` quando a validação
 * recusa antes de chamar o servidor (produto/texto vazio) — mesma convenção
 * de `submitAddMediaForm`.
 */
export function submitAddPresaleHighlightForm(
  values: PresaleHighlightFormValues,
  edit: PresaleHighlightEditHandlers
): { productId: string; headline: string } | null {
  const productId = values.get("productId");
  const headline = values.get("headline");
  if (typeof productId !== "string" || !productId) return null;
  if (typeof headline !== "string" || !headline.trim()) return null;
  const input = { productId, headline: headline.trim() };
  edit.add(input);
  return input;
}

function FlagToggle({ flag, pending, onToggle }: { flag: MarketingFeatureFlag | null; pending: boolean; onToggle: (enabled: boolean) => void }): ReactElement {
  const enabled = flag?.enabled ?? false;
  return (
    <section className="page__section flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1" data-section="faixa-flag">
      <h3 className="text-lg font-extrabold text-ink">Faixa de pré-venda</h3>
      <p className="page__note flex flex-wrap items-center gap-2 text-sm text-muted">
        <span>Status:</span>
        <span
          className={
            "inline-flex items-center gap-1.5 rounded-pill border bg-surface px-2 py-0.5 text-xs font-semibold " +
            (enabled ? "border-success text-success" : "border-border-strong text-body")
          }
          data-flag-enabled={enabled ? "on" : "off"}
        >
          {enabled ? "ligada" : "desligada"}
        </span>
      </p>
      <button
        type="button"
        disabled={pending || !flag}
        data-action="toggle-presale-ribbon"
        className={
          "inline-flex min-h-[44px] w-fit items-center justify-center rounded-control px-4 py-2 text-sm font-semibold " +
          "transition-colors duration-fast ease-s2 focus-visible:outline-none disabled:opacity-70 " +
          (enabled ? "border border-border-strong text-body hover:bg-surface-soft" : "bg-brand text-on-brand hover:bg-brand-pressed")
        }
        onClick={() => onToggle(!enabled)}
      >
        {pending ? "Salvando…" : enabled ? "Desligar faixa" : "Ligar faixa"}
      </button>
    </section>
  );
}

function HighlightActions({ highlight, edit }: { highlight: PresaleHighlight; edit?: PresaleHighlightEditHandlers }): ReactElement | null {
  if (!edit) return null;
  const BTN =
    "inline-flex min-h-[32px] items-center justify-center rounded-control border border-border-strong px-2 text-xs font-semibold text-body hover:bg-surface-soft disabled:opacity-50";
  return (
    <div className="flex flex-wrap gap-1">
      <button type="button" className={BTN} disabled={edit.pending} data-action={`presale-toggle-${highlight.id}`} onClick={() => edit.toggleActive(highlight)}>
        {highlight.active ? "Desativar" : "Ativar"}
      </button>
      <button
        type="button"
        className={`${BTN} border-danger text-danger`}
        disabled={edit.pending}
        data-action={`presale-remove-${highlight.id}`}
        onClick={() => edit.remove(highlight.id)}
      >
        Remover
      </button>
    </div>
  );
}

export function MarketingFaixaPresaleView({
  ribbonFlag,
  onToggleRibbon,
  flagPending,
  highlights,
  products,
  edit
}: MarketingFaixaPresaleViewProps): ReactElement {
  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!edit) return;
    const form = event.currentTarget;
    const result = submitAddPresaleHighlightForm(new FormData(form), edit);
    if (result) form.reset();
  }

  return (
    <div className="page page--marketing-faixa-presale flex flex-col gap-6" data-page="marketing-faixa-presale">
      <header className="flex flex-col gap-1">
        <h2 className="flex items-center gap-2 text-2xl font-extrabold text-ink">
          <Flag className="h-5 w-5 shrink-0 text-brand-text" aria-hidden focusable={false} />
          Faixa de pré-venda
        </h2>
        <p className="page__note text-sm text-muted">
          Escolha os produtos em destaque e ligue/desligue a faixa que aparece acima do header da Loja. Sem redeploy: a Loja lê direto deste cadastro.
        </p>
      </header>
      <FlagToggle flag={ribbonFlag} pending={flagPending} onToggle={onToggleRibbon} />
      <RecordTable<PresaleHighlight>
        caption="Destaques cadastrados"
        rows={highlights}
        idOf={(row) => row.id}
        emptyMessage="Nenhum destaque cadastrado ainda."
        columns={[
          { key: "headline", label: "Texto", render: (row) => row.headline },
          { key: "productId", label: "Produto", render: (row) => products.find((p) => p.productId === row.productId)?.name ?? row.productId },
          {
            key: "active",
            label: "Situação",
            render: (row) => (
              <span
                className={
                  "inline-flex items-center gap-1.5 rounded-pill border bg-surface px-2 py-0.5 text-xs font-semibold " +
                  (row.active ? "border-success text-success" : "border-border-strong text-body")
                }
                data-highlight-active={row.active ? "yes" : "no"}
              >
                {row.active ? "ativo" : "inativo"}
              </span>
            )
          },
          { key: "position", label: "Ordem", align: "right", render: (row) => String(row.position) },
          { key: "actions", label: "Ações", render: (row) => <HighlightActions highlight={row} edit={edit} /> }
        ]}
      />
      {edit ? (
        <form
          className="presale-add-form flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1"
          data-form="add-presale-highlight"
          onSubmit={onSubmit}
        >
          <h4 className="text-sm font-semibold text-ink">Adicionar destaque</h4>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label htmlFor="presale-product" className="text-sm font-semibold text-ink">
                Produto
              </label>
              <select id="presale-product" name="productId" required disabled={edit.pending} className={CONTROL} data-field="productId">
                <option value="">Selecione…</option>
                {products.map((p) => (
                  <option key={p.productId} value={p.productId}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="presale-headline" className="text-sm font-semibold text-ink">
                Texto da faixa
              </label>
              <input
                id="presale-headline"
                name="headline"
                type="text"
                required
                disabled={edit.pending}
                className={CONTROL}
                placeholder="Pré-venda de outubro: frete grátis"
                data-field="headline"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={edit.pending}
            data-action="add-presale-highlight"
            className={
              "inline-flex min-h-[44px] w-fit items-center justify-center rounded-control bg-brand px-4 py-2 " +
              "text-sm font-semibold text-on-brand transition-colors duration-fast ease-s2 " +
              "hover:bg-brand-pressed focus-visible:outline-none disabled:opacity-70"
            }
          >
            {edit.pending ? "Gravando…" : "Adicionar destaque"}
          </button>
        </form>
      ) : (
        <StatusPanel kind="awaiting_source" title="Gravação de destaque não montada nesta visualização" detail="A lista acima reflete o banco; esta montagem é só leitura." />
      )}
      {edit?.error ? <StatusPanel kind="error" title="A faixa não foi atualizada" detail={edit.error.message} /> : null}
    </div>
  );
}

export function MarketingFaixaPresale(): ReactElement {
  const flagsQuery = useMarketingFeatureFlags();
  const highlightsQuery = usePresaleHighlights();
  const catalogQuery = useCatalogDataset();
  const setFlag = useSetMarketingFeatureFlag();
  const addHighlight = useAddPresaleHighlight();
  const updateHighlight = useUpdatePresaleHighlight();
  const removeHighlight = useRemovePresaleHighlight();

  if (flagsQuery.isPending || highlightsQuery.isPending || catalogQuery.isPending) {
    return <StatusPanel kind="loading" title="Carregando faixa de pré-venda…" />;
  }
  if (flagsQuery.error || highlightsQuery.error || catalogQuery.error) {
    return (
      <StatusPanel kind="error" title="Não foi possível carregar a faixa de pré-venda" detail={errorDetail(flagsQuery.error ?? highlightsQuery.error ?? catalogQuery.error)} />
    );
  }

  const ribbonFlag = (flagsQuery.data ?? []).find((f) => f.featureKey === "presale_ribbon") ?? null;
  const products = (catalogQuery.data?.products ?? []).map((p) => ({ productId: p.productId, name: p.name }));

  const pending = addHighlight.isPending || updateHighlight.isPending || removeHighlight.isPending;
  const error = addHighlight.error ?? updateHighlight.error ?? removeHighlight.error ?? null;

  return (
    <MarketingFaixaPresaleView
      ribbonFlag={ribbonFlag}
      flagPending={setFlag.isPending}
      onToggleRibbon={(enabled) => setFlag.mutate({ featureKey: "presale_ribbon", enabled })}
      highlights={highlightsQuery.data ?? []}
      products={products}
      edit={{
        add: (input) => addHighlight.mutate(input),
        toggleActive: (highlight) => updateHighlight.mutate({ id: highlight.id, patch: { active: !highlight.active } }),
        remove: (id) => removeHighlight.mutate({ id }),
        pending,
        error
      }}
    />
  );
}
